import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { fmtDate } from '../lib/format';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Star, MessageSquareHeart, QrCode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PageHelp from '../components/PageHelp';

const Stars = ({ n }) => (
  <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-3.5 w-3.5 ${i <= n ? 'text-amber-500 fill-amber-500' : 'text-muted'}`} />)}</span>
);

export default function FeedbackList() {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  useEffect(() => { (async () => { const { data } = await api.get('/feedback'); setRows(data); })(); }, []);

  const avg = rows.length ? (rows.reduce((s, r) => s + (r.rating || 0), 0) / rows.length) : 0;
  const feedbackUrl = `${window.location.origin}/feedback/${tenant?.id}`;

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="font-display text-2xl font-bold">Guest Feedback</h1><p className="text-sm text-muted-foreground">{rows.length} responses · avg rating {avg ? avg.toFixed(1) : '—'}</p></div>
        <Button variant="outline" data-testid="feedback-qr-link" onClick={() => navigate('/settings')}>
          <QrCode className="h-4 w-4 mr-1" /> Get Feedback QR (Settings → QR Ordering)
        </Button>
      </div>
      <PageHelp id="feedback">Guests scan the feedback QR (print it from Settings → QR Ordering) to rate their visit. Responses appear here in real time.</PageHelp>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4"><div className="text-sm text-muted-foreground">Responses</div><div className="text-xl font-display font-bold" data-testid="feedback-count">{rows.length}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Average Rating</div><div className="text-xl font-display font-bold flex items-center gap-2" data-testid="feedback-avg">{avg ? avg.toFixed(1) : '—'} {avg > 0 && <Stars n={Math.round(avg)} />}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">5-Star</div><div className="text-xl font-display font-bold">{rows.filter((r) => r.rating === 5).length}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Needs Attention (≤2★)</div><div className="text-xl font-display font-bold text-destructive">{rows.filter((r) => r.rating && r.rating <= 2).length}</div></Card>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Rating</TableHead><TableHead>Comment</TableHead><TableHead>Phone</TableHead><TableHead>Order</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`feedback-row-${r.id}`}>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                <TableCell><Stars n={r.rating || 0} /></TableCell>
                <TableCell className="text-sm max-w-md">{r.comment || <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-sm">{r.customer_phone || '—'}</TableCell>
                <TableCell>{r.order_no ? <Badge variant="secondary">{r.order_no}</Badge> : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && <div className="p-8 text-center text-muted-foreground"><MessageSquareHeart className="h-8 w-8 mx-auto mb-2 opacity-40" />No feedback yet — share your QR: {feedbackUrl}</div>}
      </Card>
    </div>
  );
}
